import { AxiosError, isAxiosError } from 'axios'

const NON_FIELD_ERROR_ATTRS = ['detail', 'non_field_errors']

// https://drf-standardized-errors.readthedocs.io/en/latest/error_response.html#error-response-format
type ErrorData = {
  type: 'validation_error' | 'client_error' | 'server_error'
  errors: {
    code: string
    detail: string
    attr?: string // only set for validation_error type, maps to serializer field name
  }[]
}

export type ApiError = {
  status?: number
  errorMessage?: string
  data?: ErrorData
  axiosError?: AxiosError
}

const getErrorMessage = (
  data: ErrorData | undefined,
  includeFieldNameInValidationErrors?: boolean
): string => {
  if (!data?.errors?.length) {
    return ''
  }

  const { attr, detail } = data.errors[0]

  // Return the detail directly for non-field errors. Otherwise, include the attribute in brackets.
  // Example: '[first_name] this field is required'.
  if (
    !attr ||
    NON_FIELD_ERROR_ATTRS.includes(attr) ||
    !includeFieldNameInValidationErrors
  ) {
    return detail
  }

  return `[${attr}] ${detail}`
}

export type ErrorMessageOptions = {
  includeFieldNameInValidationErrors?: boolean
}

// This utility function extracts the HTTP status and error payload from an Axios error response.
// It also retrieves any server-generated error messages for display to the user, if available.
export const getApiError = (error: unknown, options?: ErrorMessageOptions) => {
  if (isAxiosError(error)) {
    const data = error.response?.data as ErrorData | undefined

    return {
      status: error.response?.status,
      errorMessage: getErrorMessage(
        data,
        options?.includeFieldNameInValidationErrors ?? true
      ),
      data,
      axiosError: error // attach raw axios arror
    }
  }

  return {}
}
